// API client with automatic JWT token injection
// Intercepts all fetch requests to add Authorization header

import { useAuthStore } from '../stores/useAuthStore';
import { isCloudMode } from './cloudMode';

// Store the original fetch function
const originalFetch = globalThis.fetch;

const isCloud = isCloudMode();

// In cloud mode, /__mockforge/ endpoints don't exist on the registry server.
// The main API services (dashboard, workspaces, services, fixtures, federation)
// now use /api/v1/ endpoints directly. Components that still reference /__mockforge/
// paths get shaped stub responses to prevent crashes.
function createCloudStubResponse(url: string): Response {
  const path = new URL(url, window.location.origin).pathname;

  // Reality level — needed by RealitySlider component
  if (path === '/__mockforge/reality/level') {
    return new Response(JSON.stringify({ success: true, data: {
      level: 1, level_name: 'Cloud', description: 'Cloud-hosted mock',
      chaos: { enabled: false, error_rate: 0, delay_rate: 0 },
      latency: { base_ms: 0, jitter_ms: 0 },
      mockai: { enabled: false },
    }}), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // Time travel — needed by TimeTravelWidget
  if (path.startsWith('/__mockforge/time-travel')) {
    return new Response(JSON.stringify({ success: true, data: {
      enabled: false, current_time: new Date().toISOString(), scale: 1.0,
      mutations: [], cron_jobs: [],
    }}), { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // Reality presets
  if (path.startsWith('/__mockforge/reality/presets')) {
    return new Response(JSON.stringify({ success: true, data: [] }),
      { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // Logs — needed by RequestLog component
  if (path === '/__mockforge/logs' || path.startsWith('/__mockforge/logs?')) {
    return new Response(JSON.stringify({ success: true, data: [] }),
      { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // Routes — needed by Services page in local mode
  if (path === '/__mockforge/routes') {
    return new Response(JSON.stringify({ success: true, data: [] }),
      { status: 200, headers: { 'Content-Type': 'application/json' } });
  }

  // Generic fallback for any other /__mockforge/ paths
  return new Response(JSON.stringify({ success: true, data: {} }),
    { status: 200, headers: { 'Content-Type': 'application/json' } });
}

// Local-only API paths that don't exist on the registry server.
// In cloud mode, return stubs instead of letting them 404.
const LOCAL_ONLY_API_PREFIXES = [
  '/api/chaos/',
  '/api/observability/',
  '/api/resilience/',
  '/api/recorder/',
  '/api/conformance/',
  '/api/performance/',
  '/api/world-state/',
  '/api/v1/consistency/',
  '/api/v1/drift/',
  '/api/v1/scenario-studio/',
  '/api/v2/analytics/',
];

// Cached server capabilities (fetched once from GET /api/capabilities)
let cachedCapabilities: string[] | null = null;

/** Fetch available features from the server's capabilities endpoint. */
export async function fetchCapabilities(baseUrl?: string): Promise<string[]> {
  if (cachedCapabilities) return cachedCapabilities;
  try {
    const url = baseUrl
      ? `${baseUrl}/api/capabilities`
      : '/api/capabilities';
    const res = await originalFetch(url);
    if (res.ok) {
      const data = await res.json();
      const features: string[] = data.features ?? [];
      cachedCapabilities = features;
      return features;
    }
  } catch {
    // Server may not support capabilities yet — fall back to static list
  }
  return [];
}

/** Check whether a feature is reported as available by the server. */
export function hasCapability(feature: string): boolean {
  return cachedCapabilities?.includes(feature) ?? false;
}

function isLocalOnlyApi(url: string): boolean {
  const path = new URL(url, window.location.origin).pathname;
  return LOCAL_ONLY_API_PREFIXES.some(prefix => path.startsWith(prefix));
}

function createLocalApiStubResponse(_url: string): Response {
  return new Response(JSON.stringify({}),
    { status: 200, headers: { 'Content-Type': 'application/json' } });
}

// Create a fetch wrapper that adds JWT tokens
export function createAuthenticatedFetch() {
  return async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

    // In cloud mode, intercept local-only endpoints with stub responses
    if (isCloud && url.includes('/__mockforge/')) {
      return createCloudStubResponse(url);
    }
    if (isCloud && isLocalOnlyApi(url)) {
      return createLocalApiStubResponse(url);
    }

    const state = useAuthStore.getState();
    const token = state.token;

    // Clone the init object to avoid mutating the original
    const headers = new Headers(init?.headers);

    // Add Authorization header if token exists
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }

    // Create new init with updated headers
    const newInit: RequestInit = {
      ...init,
      headers,
    };

    // Make the request
    let response = await originalFetch(input, newInit);

    // Handle 401 Unauthorized - token might be expired
    const requestPath = new URL(url, window.location.origin).pathname;
    if (response.status === 401 && (token || (isCloud && state.isAuthenticated)) && !requestPath.startsWith('/api/v1/auth/')
      && !requestPath.startsWith('/__mockforge/auth/')) {
      try {
        // Try to refresh the token
        // A concurrent request may already have rotated this token.
        if (useAuthStore.getState().token === token) {
          await state.refreshTokenAction();
        }

        // Retry the request with new token
        const newToken = useAuthStore.getState().token;
        if (newToken) {
          headers.set('Authorization', `Bearer ${newToken}`);
          response = await originalFetch(input, { ...newInit, headers });
        }
      } catch {
        // The store clears a rejected refresh session; preserve newer logins.
      }
    }

    return response;
  };
}

// Export a fetch function that can be used throughout the app
export const authenticatedFetch = createAuthenticatedFetch();

// In cloud mode, override global fetch to intercept /__mockforge/ calls everywhere.
// This catches components that use raw fetch() instead of authenticatedFetch.
if (isCloud) {
  globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const url = typeof input === 'string' ? input : input instanceof URL ? input.href : (input as Request).url;
    if (url.includes('/__mockforge/')) {
      return createCloudStubResponse(url);
    }
    if (isLocalOnlyApi(url)) {
      return createLocalApiStubResponse(url);
    }
    return originalFetch(input, init);
  };
}
